import { FC, useState } from "react";
import { useAtom } from "jotai";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { i18n } from "@/i18n";

import { OptionType } from "../blocks";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithOptions } from "./DriverScreenWithOptions";

type ValueType = "YES" | "NO";

const CAN_DROP_BIN_OPTIONS: OptionType<ValueType>[] = [
  {
    label: i18n.t("yes"),
    value: "YES",
  },
  {
    label: i18n.t("no"),
    value: "NO",
  },
];

const DriverCanDropBin: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const [canDropBin, setCanDropBin] = useState<ValueType | null>(null);

  const [isDriverReviewingTask, setIsDriverReviewingTask] = useAtom(
    isDriverReviewingTaskAtom,
  );

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const isChangingOriginalSelection =
    (canDropBin === "YES" && Boolean(serviceData?.turnaroundPhoto)) || // If there's turnaroundPhoto, it means the original option was NO
    (canDropBin === "NO" && Boolean(serviceData?.siteDropPhoto)); // If there's siteDropPhoto, it means the original option was YES

  const onNextClick = () => {
    let driverFlowStatus = TaskDriverStatusEnum.TASK_REVIEW;

    // If it's the first time choosing if can drop bin or it's changing the original selection from the review screen
    if (
      !isDriverReviewingTask ||
      (isDriverReviewingTask && isChangingOriginalSelection)
    ) {
      // If it's changing the original selection, resets the isDriverReviewingTask atom value
      if (isDriverReviewingTask && isChangingOriginalSelection) {
        setIsDriverReviewingTask(false);
      }

      if (canDropBin === "YES") {
        driverFlowStatus = TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE;
      } else {
        driverFlowStatus = TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN;
      }
    }

    updateService({
      jobId,
      serviceId,
      driverFlowStatus,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isNextButtonLoading = isUpdatingService || isUpdatingTask;

  return (
    <DriverScreenWithOptions
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_can_drop_bin.title"
      optionsSelectorProps={{
        options: CAN_DROP_BIN_OPTIONS,
        onSelect: setCanDropBin,
        value: canDropBin,
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle:
          isDriverReviewingTask && !isChangingOriginalSelection // Only get back to the review screen if it's not changing the original selection
            ? "review"
            : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !canDropBin,
      }}
    />
  );
};

export { DriverCanDropBin };
